import React, { useEffect, useState } from "react";
import FeedIcon from "@mui/icons-material/Feed";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Badge from "@mui/material/Badge";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem, Skeleton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import InputDialog from "./InputDialog.tsx";
import EditDialog from "./EditDialog.tsx";
import ConfirmDialog from "./ConfirmDialog.tsx";
import Notification from "./Notification.tsx";
import { MessageGroup, MessageItem, CollaborationInstance } from "../../models/LeftPanel";
import "../../styles/LeftPanelLegacy.css";
import { createChat, createInstance, renameInstance, deleteInstance } from "../../resources/api.resource.ts";
import { UserMapValue } from "../../models/User.ts";

const iconMap = { GroupsIcon, FeedIcon, PersonIcon } as const;
type IconMapKey = keyof typeof iconMap;

function DynamicIcon({ iconName, ...props }: { iconName: string } & SvgIconProps) {
  const IconComponent = iconMap[iconName as IconMapKey] ?? iconMap["FeedIcon"];
  return <IconComponent {...props} />;
}

interface LeftPanelProps {
  MessagesArray: Array<MessageGroup>;
  onSelectItem: (item: MessageItem, sectionName: string) => void;
  onGroupCreated?: (groupData: MessageItem) => void;
  onInstanceSelect?: (instance: CollaborationInstance | null) => void;
  onInstanceCreated?: (newInstance: CollaborationInstance, parentItem: MessageItem, sectionName: string) => void;
  onInstanceRenamed?: (instance: CollaborationInstance, newTopic: string, parentItem: MessageItem, sectionName: string) => void;
  onInstanceDeleted?: (instanceId: string, parentItem: MessageItem, sectionName: string) => void;
  allUsersMap: Record<string, UserMapValue>;
  currentUser: any;
  selectedItem: any;
  isLoading?: boolean;
  hideEmptySections?: boolean;
  onMentionCleared?: (collaborationId: string, collaborationIdSummary?: string) => void;
  draftGroupIds?: Set<string>;
  onStartNewDM?: () => void;
}

const LeftPanelLegacy = ({
  MessagesArray,
  onSelectItem,
  onGroupCreated,
  onInstanceSelect,
  onInstanceCreated,
  onInstanceRenamed,
  onInstanceDeleted,
  allUsersMap,
  currentUser,
  selectedItem,
  isLoading,
  hideEmptySections = false,
  onMentionCleared,
  draftGroupIds,
}: LeftPanelProps) => {
  const [messages, setMessages] = useState<MessageGroup[]>([]);
  const [menuOpen, setMenuOpen] = useState<boolean[]>([]);
  const [idSelected, setIdSelected] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notif, setNotif] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Collapsible list state
  const [expandedInstances, setExpandedInstances] = useState<Set<string>>(new Set());

  // Track how many instances to show per item (for "Show more" functionality)
  const [visibleInstancesCount, setVisibleInstancesCount] = useState<Record<string, number>>({});


  // Selected instance tracking
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  // Track the last auto-expanded item to prevent re-expansion on every render
  const lastAutoExpandedRef = React.useRef<string | null>(null);

  // Instance action states
  const [isCreatingInstance, setIsCreatingInstance] = useState(false);
  const [hoveredInstanceId, setHoveredInstanceId] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [instanceToRename, setInstanceToRename] = useState<{
    instance: CollaborationInstance;
    parentItem: MessageItem;
    sectionName: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<{
    instance: CollaborationInstance;
    parentItem: MessageItem;
    sectionName: string;
  } | null>(null);

   useEffect(() => {
    if(selectedItem === null) {
      setIdSelected(null);
      setSelectedInstanceId(null);
      lastAutoExpandedRef.current = null;
    } else if(selectedItem?.collaborationId) {
      setIdSelected(selectedItem.collaborationId);

      // Only auto-expand if this is a new selection (not already processed)
      if (lastAutoExpandedRef.current === selectedItem.collaborationId) {
        return; // Already processed this selection, don't re-expand
      }

      // Find the item in messages to check if it has instances
      let foundItem: MessageItem | null = null;
      let foundSection = '';

      for (const group of messages) {
        const item = group.items.find(
          i => i.collaborationId === selectedItem.collaborationId ||
               i.collaborationIdSummary === selectedItem.collaborationId ||
               (i.collab_instances || []).some(inst => inst.id === selectedItem.collaborationId)
        );
        if (item) {
          foundItem = item;
          foundSection = group.name;
          break;
        }
      }

      if (foundItem && allowsMultipleInstances(foundItem)) {
        const expansionKey = getExpansionKey(foundItem);

        // Check if the selected item is an instance
        const matchingInstance = (foundItem.collab_instances || []).find(
          inst => inst.id === selectedItem.collaborationId
        );

        if (matchingInstance) {
          // Selected item is an instance, expand and select it
          setExpandedInstances(prev => {
            const newSet = new Set(prev);
            newSet.add(expansionKey);
            return newSet;
          });
          setSelectedInstanceId(matchingInstance.id);
          // Notify parent with instance details
          const instanceItem: MessageItem = {
            ...foundItem,
            collaborationId: matchingInstance.id,
            name: matchingInstance.topic || foundItem.name,
          };
          onSelectItem(instanceItem, foundSection);
          onInstanceSelect?.(matchingInstance);
          lastAutoExpandedRef.current = selectedItem.collaborationId;
        } else if (foundItem.collab_instances && foundItem.collab_instances.length > 0) {
          // Selected item is the parent, auto-expand and select first instance
          setExpandedInstances(prev => {
            const newSet = new Set(prev);
            newSet.add(expansionKey);
            return newSet;
          });
          const firstInstance = foundItem.collab_instances[0];
          setSelectedInstanceId(firstInstance.id);
          // Notify parent to select the first instance
          const instanceItem: MessageItem = {
            ...foundItem,
            collaborationId: firstInstance.id,
            name: firstInstance.topic || foundItem.name,
          };
          onSelectItem(instanceItem, foundSection);
          onInstanceSelect?.(firstInstance);
          lastAutoExpandedRef.current = selectedItem.collaborationId;
        }
      }
    }
  }, [selectedItem, messages]);

  useEffect(() => {
    const defaults: MessageGroup[] = [
      { name: "Direct Messages", items: [] },
      { name: "Groups", items: [] },
      { name: "Projects", items: [] },
    ];

    if (MessagesArray && MessagesArray.length > 0) {
      MessagesArray.forEach((group) => {
        const section = defaults.find((s) => s.name === group.name);
        if (section) section.items.push(...group.items);
        else defaults.push(group);
      });
    }

    // Sort Direct Messages: DMs with agents first, then DMs with humans
    const dmSection = defaults.find((s) => s.name === "Direct Messages");
    if (dmSection && dmSection.items.length > 0) {
      dmSection.items.sort((a, b) => {
        // Find user in allUsersMap by matching the DM name
        const aUser = Object.values(allUsersMap).find(
          (user: any) => user.name?.toLowerCase() === a.name?.toLowerCase()
        );
        const aHasAgent = aUser?.userType === "agent";

        const bUser = Object.values(allUsersMap).find(
          (user: any) => user.name?.toLowerCase() === b.name?.toLowerCase()
        );
        const bHasAgent = bUser?.userType === "agent";

        // Sort agents first (return -1 if a has agent and b doesn't)
        if (aHasAgent && !bHasAgent) return -1;
        if (!aHasAgent && bHasAgent) return 1;
        return 0;
      });
    }

    setMessages(defaults);
    if(menuOpen.length === 0) {
      setMenuOpen(defaults.map(() =>  true));
    }

  }, [MessagesArray, allUsersMap]);

  const handleCollapse = (index: number) =>
    setMenuOpen((prev) => prev.map((c, i) => (i === index ? !c : c)));

  // Helper to get stable expansion key (parent collaboration ID)
  const getExpansionKey = (item: MessageItem): string => {
    return item.collaborationIdSummary || item.collaborationId;
  };

  const handleClickPanel = (item: MessageItem, sectionName: string) => {
    setIdSelected(item.collaborationId);

    // If allows multiple instances, auto-expand instead of selecting main
    if (allowsMultipleInstances(item) && item.collab_instances && item.collab_instances.length > 0) {
      const expansionKey = getExpansionKey(item);
      setExpandedInstances(prev => {
        const newSet = new Set(prev);
        newSet.add(expansionKey);
        return newSet;
      });
      // Don't call onSelectItem for the main item - user must select an instance
      return;
    }

    setSelectedInstanceId(null); // Clear instance selection when clicking on item
    onSelectItem(item, sectionName);
    onInstanceSelect?.(null); // Notify parent that no instance is selected
  };

  // Check if a collaboration allows multiple instances (has an agent member)
  const allowsMultipleInstances = (item: MessageItem): boolean => {
    // Check if any member is an agent
    if (item.memberUids && item.memberUids.length > 0) {
      return item.memberUids.some(uid => {
        const user = allUsersMap[uid.toLowerCase()];
        return user?.userType === 'agent';
      });
    }
    // Fallback: check by item name (original DM logic)
    const userName = item.name?.toLowerCase();
    const user = Object.values(allUsersMap).find(
      (u: UserMapValue) => u.name?.toLowerCase() === userName
    );
    return user?.userType === 'agent';
  };

  // Toggle instance expansion (uses expansion key)
  const toggleInstancesExpansion = (expansionKey: string) => {
    setExpandedInstances(prev => {
      const newSet = new Set(prev);
      if (newSet.has(expansionKey)) {
        newSet.delete(expansionKey);
      } else {
        newSet.add(expansionKey);
      }
      return newSet;
    });
  };


  // Handle instance selection
  const handleInstanceClick = (item: MessageItem, instance: CollaborationInstance, sectionName: string) => {
    setSelectedInstanceId(instance.id);
    // Create a modified item with the instance's id and topic as name
    const instanceItem: MessageItem = {
      ...item,
      collaborationId: instance.id,
      name: item.name,
    };
    onSelectItem(instanceItem, sectionName);
    onInstanceSelect?.(instance);
    if (item.hasMention) {
      onMentionCleared?.(item.collaborationId, item.collaborationIdSummary);
    }
  };

  // Create new instance
  const handleCreateInstance = async (item: MessageItem, sectionName: string) => {
    if (isCreatingInstance) return;
    setIsCreatingInstance(true);
    const collaborationIdSummary = item.collaborationIdSummary || item.collaborationId;
    try {
      const response = await createInstance({
        collaborationId: collaborationIdSummary,
        topic: 'New chat'
      });

      // Create the new instance object from response
      const newInstance: CollaborationInstance = {
        id: response.id || response.instanceId,
        topic: response.topic || 'New chat',
        status: response.status || 'active'
      };

      // Select the new instance immediately (local state)
      setSelectedInstanceId(newInstance.id);
      setIdSelected(newInstance.id);

      // Ensure the list stays expanded
      const expansionKey = getExpansionKey(item);
      setExpandedInstances(prev => {
        const newSet = new Set(prev);
        newSet.add(expansionKey);
        return newSet;
      });

      setNotif({
        open: true,
        message: 'New chat created successfully',
        severity: 'success'
      });

      // Notify parent to handle selection and refresh (parent will call onSelectItem)
      onInstanceCreated?.(newInstance, item, sectionName);
    } catch (err: any) {
      setNotif({
        open: true,
        message: err.message || 'Failed to create new chat',
        severity: 'error'
      });
    } finally {
      setIsCreatingInstance(false);
    }
  };

  // Open rename dialog
  const handleOpenRenameDialog = (
    e: React.MouseEvent,
    instance: CollaborationInstance,
    parentItem: MessageItem,
    sectionName: string
  ) => {
    e.stopPropagation();
    setInstanceToRename({ instance, parentItem, sectionName });
    setRenameDialogOpen(true);
  };

  // Handle rename confirmation
  const handleRenameInstance = async (newTopic: string) => {
    if (!instanceToRename) return;

    const { instance, parentItem, sectionName } = instanceToRename;
    const collaborationId = parentItem.collaborationIdSummary || parentItem.collaborationId;

    try {
      await renameInstance({ collaborationId, collabInstanceId: instance.id, topic: newTopic });

      // Update local state - find and update the instance topic
      setMessages(prevMessages => {
        return prevMessages.map(group => {
          if (group.name !== sectionName) return group;
          return {
            ...group,
            items: group.items.map(item => {
              const itemKey = item.collaborationIdSummary || item.collaborationId;
              const parentKey = parentItem.collaborationIdSummary || parentItem.collaborationId;
              if (itemKey !== parentKey) return item;
              return {
                ...item,
                collab_instances: (item.collab_instances || []).map(inst =>
                  inst.id === instance.id ? { ...inst, topic: newTopic } : inst
                )
              };
            })
          };
        });
      });

      setNotif({
        open: true,
        message: 'Chat renamed successfully',
        severity: 'success'
      });

      // Notify parent for any additional handling
      onInstanceRenamed?.({ ...instance, topic: newTopic }, newTopic, parentItem, sectionName);
    } catch (err: any) {
      setNotif({
        open: true,
        message: err.message || 'Failed to rename chat',
        severity: 'error'
      });
    } finally {
      setRenameDialogOpen(false);
      setInstanceToRename(null);
    }
  };

  // Open delete dialog
  const handleOpenDeleteDialog = (
    e: React.MouseEvent,
    instance: CollaborationInstance,
    parentItem: MessageItem,
    sectionName: string
  ) => {
    e.stopPropagation();
    setInstanceToDelete({ instance, parentItem, sectionName });
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteInstance = async () => {
    if (!instanceToDelete) return;

    const { instance, parentItem, sectionName } = instanceToDelete;
    const collaborationId = parentItem.collaborationIdSummary || parentItem.collaborationId;
    const wasSelected = selectedInstanceId === instance.id;

    try {
      await deleteInstance(collaborationId, instance.id);

      // Update local state - remove the instance
      setMessages(prevMessages => {
        return prevMessages.map(group => {
          if (group.name !== sectionName) return group;
          return {
            ...group,
            items: group.items.map(item => {
              const itemKey = item.collaborationIdSummary || item.collaborationId;
              const parentKey = parentItem.collaborationIdSummary || parentItem.collaborationId;
              if (itemKey !== parentKey) return item;

              const remainingInstances = (item.collab_instances || []).filter(
                inst => inst.id !== instance.id
              );

              // If the deleted instance was selected, select the first remaining instance
              if (wasSelected && remainingInstances.length > 0) {
                setSelectedInstanceId(remainingInstances[0].id);
                handleInstanceClick(item, remainingInstances[0], sectionName);
              } else if (wasSelected && remainingInstances.length === 0) {
                setSelectedInstanceId(null);
                onInstanceSelect?.(null);
              }

              return {
                ...item,
                collab_instances: remainingInstances
              };
            })
          };
        });
      });

      setNotif({
        open: true,
        message: 'Chat deleted successfully',
        severity: 'success'
      });

      // Notify parent
      onInstanceDeleted?.(instance.id, parentItem, sectionName);
    } catch (err: any) {
      setNotif({
        open: true,
        message: err.message || 'Failed to delete chat',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setInstanceToDelete(null);
    }
  };

  const handleMenuClick = (section: string, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setCurrentSection(section);
  };

  const handleMenuAction = (action: string) => {
    if (["Create Group", "Add Project", "Start DM"].includes(action))
      setDialogOpen(true);
    setMenuAnchor(null);
  };


  const handleDialogSubmit = async (name: string, members: string[]) => {
    try {
      const type =
        currentSection === "Projects"
          ? "project"
          : currentSection === "Direct Messages"
            ? "dm"
            : "group";

      const memberDetails = members.map((uid) => ({
        uid,
        name: allUsersMap[uid.toLowerCase()]?.name || "Unknown",
        email:
          allUsersMap[uid.toLowerCase()]?.email || "unknown@example.com",
        userType: allUsersMap[uid.toLowerCase()]?.userType || "user",
      }));

      const payload: any = { type, memberUids: members, memberDetails };
      if (type !== "dm") payload.name = name;

      const res = await createChat(payload);

      // if (!res.ok) {
      //   const err = await res;
      //   throw new Error(err.detail || "Failed to create collaboration");
      // }

      const data = await res;
      const sectionName = currentSection || "Groups";

      const newItem: MessageItem = {
        collaborationId: data.collaborationId,
        groupId: data.groupId || data.collaborationId,
        name: data.name || name,
        members: data.members || members,
        iconName:
          type === "project"
            ? "FeedIcon"
            : type === "group"
              ? "GroupsIcon"
              : "PersonIcon",
        notificationCount: 0,
        unread: false,
        avatar: undefined,
        selected: false,
      };

      if (onGroupCreated) onGroupCreated(newItem);

      setDialogOpen(false);
      setNotif({
        open: true,
        message: `${name} created successfully!`,
        severity: "success",
      });
    } catch (err: any) {
      setNotif({
        open: true,
        message: err.message || "Failed to create collaboration",
        severity: "error",
      });
    }
  };


  const getMenuOptions = (section: string) => {
    if (section === "Groups") return ["Create Group"];
    if (section === "Projects") return ["Add Project"];
    if (section === "Direct Messages") return ["Start DM"];
    return [];
  };

  const getTitle = (name: string) => {
    const isMenuOpen = currentSection === name && Boolean(menuAnchor);
    return (
      <div
        className="titleGroupDiv"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
      >
        <div className="titleGroup">{name.toUpperCase()}</div>
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(name, e)}
          className="moreVertIconWrapper"
          sx={{
            opacity: isMenuOpen ? 1 : 0,
            transition: 'opacity 0.2s ease',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <MoreVertIcon sx={{ fontSize: '1.1rem', color: '#666' }} />
        </IconButton>
      </div>
    );
  };

  const getMentionBadge = (hasMention: boolean) =>
    hasMention ? (
      <Badge
        badgeContent="@"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#0857c3',
            color: 'white',
            fontSize: '0.65rem',
            fontWeight: 700,
            minWidth: '18px',
            height: '18px',
            padding: '0 4px',
          }
        }}
      />
    ) : null;

  const getMessageCount = (count: number, unread: boolean) =>
    count > 0 ? (
      <Badge
        badgeContent={count}
        className={unread ? "" : "defaultBadge"}
        color={unread ? "error" : "default"}
      />
    ) : null;

  const getIconAvatar = (item: MessageItem) =>
    item.avatar ? (
      <img src={item.avatar} alt={item.name} className="avatarImage" />
    ) : (
      <DynamicIcon
        iconName={item.iconName ?? "FeedIcon"}
        sx={{ minWidth: "35px", fontSize: "1.3rem" }}
      />
    );

  const getListItems = () => {
    const displayMessages = hideEmptySections
      ? messages.filter((group) =>
          // Always show Direct Messages section, even if empty, so users can start new DMs
          group.name === "Direct Messages" || (group.items && group.items.length > 0)
        )
      : messages;
    return displayMessages.map((group, idx) => (
      <React.Fragment key={idx}>
        <ListItem disablePadding className={`${group.name.charAt(0)}div`}>
          <ListItemButton onClick={() => handleCollapse(idx)}>
            <ListItemText primary={getTitle(group.name)} />
            {menuOpen[idx] ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          className={`${group.name.charAt(0)}div`}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              mt: 0.5,
              minWidth: '180px'
            }
          }}
        >
          {getMenuOptions(currentSection).map((opt) => {
            const IconComponent =
              opt === "Create Group" ? GroupsIcon :
              opt === "Add Project" ? FeedIcon :
              PersonIcon;
            return (
              <MenuItem
                key={opt}
                onClick={() => handleMenuAction(opt)}
                sx={{
                  py: 1.25,
                  px: 2,
                  gap: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <IconComponent sx={{ fontSize: '1.2rem', color: '#666' }} />
                {opt}
              </MenuItem>
            );
          })}
        </Menu>

        {group.items && group.items.length > 0 && (
          <Collapse in={menuOpen[idx]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className={`${group.name.charAt(0)}div`} >
              {group.items.map((item, itemIdx) => (
                <React.Fragment key={itemIdx}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getMentionBadge(item.hasMention || false)}
                        {getMessageCount(item.notificationCount, item.unread || false)}
                        {allowsMultipleInstances(item) && (
                          <Tooltip title="Show instances">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleInstancesExpansion(getExpansionKey(item));
                              }}
                            >
                              {expandedInstances.has(getExpansionKey(item)) ?
                                <ExpandLess fontSize="small" /> :
                                <ChevronRightIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton
                      selected={
                        idSelected === item.collaborationId && !selectedInstanceId
                      }
                      onClick={() => handleClickPanel(item, group.name)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(235, 31, 61, 0.08)',
                          borderLeft: '3px solid #EB1F3D',
                          '&:hover': {
                            backgroundColor: 'rgba(235, 31, 61, 0.12)',
                          }
                        }
                      }}
                    >
                      <ListItemIcon>{getIconAvatar(item)}</ListItemIcon>
                      <ListItemText
                        disableTypography
                        primary={item.name}
                        secondary={
                          item.members && item.members.length > 0 ? (
                            <div>
                              Members:{" "}
                              {item.members
                                .map((m) => (typeof m === "string" ? m : m.name))
                                .join(", ")}
                            </div>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {/* Collapsible Instances List */}
                  {allowsMultipleInstances(item) && expandedInstances.has(getExpansionKey(item)) && (
                    <Collapse in={true} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {/* Add New Chat Button */}
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleCreateInstance(item, group.name)}
                            disabled={isCreatingInstance}
                            sx={{
                              borderRadius: '6px',
                              color: 'primary.main',
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                borderStyle: 'solid',
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <AddIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary="New Chat"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                textTransform: 'uppercase'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {/* Instances List */}
                        {(() => {
                          const expansionKey = getExpansionKey(item);
                          const instances = (item.collab_instances || []).filter(inst => inst.status === 'active');
                          const visibleCount = visibleInstancesCount[expansionKey] || 5;
                          const visibleInstances = instances.slice(0, visibleCount);
                          const hasMore = instances.length > visibleCount;
                          const remainingCount = instances.length - visibleCount;

                          return (
                            <>
                              {visibleInstances.map((instance) => (
                                <ListItem
                                  key={instance.id}
                                  disablePadding
                                  onMouseEnter={() => setHoveredInstanceId(instance.id)}
                                  onMouseLeave={() => setHoveredInstanceId(null)}
                                  secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {draftGroupIds?.has(instance.id) && (
                                        <Tooltip title="Draft saved">
                                          <EditNoteIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                                        </Tooltip>
                                      )}
                                      {(instance.unreadCount ?? 0) > 0 && (
                                        <Box sx={{ width: 20, height: 20, position: 'relative', flexShrink: 0 }}>
                                          <Badge
                                            badgeContent={instance.unreadCount}
                                            sx={{
                                              '& .MuiBadge-badge': {
                                                backgroundColor: '#9e9e9e',
                                                color: 'white',
                                                position: 'relative',
                                                transform: 'none',
                                                transformOrigin: 'unset',
                                              }
                                            }}
                                          />
                                        </Box>
                                      )}
                                      {hoveredInstanceId === instance.id && (
                                        <>
                                          <Tooltip title="Rename">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => handleOpenRenameDialog(e, instance, item, group.name)}
                                              sx={{
                                                padding: '2px',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                                              }}
                                            >
                                              <EditIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => handleOpenDeleteDialog(e, instance, item, group.name)}
                                              sx={{
                                                padding: '2px',
                                                '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                                              }}
                                            >
                                              <DeleteOutlineIcon fontSize="small" sx={{ fontSize: '0.9rem', color: 'error.main' }} />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Box>
                                  }
                                >
                                  <ListItemButton
                                    selected={selectedInstanceId === instance.id}
                                    onClick={() => handleInstanceClick(item, instance, group.name)}
                                    sx={{
                                      '&.Mui-selected': {
                                        backgroundColor: 'rgba(235, 31, 61, 0.08)',
                                        borderLeft: '3px solid #EB1F3D',
                                        '&:hover': {
                                          backgroundColor: 'rgba(235, 31, 61, 0.12)',
                                        }
                                      },
                                      pr: hoveredInstanceId === instance.id ? '80px' : '36px'
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                      <ChatBubbleOutlineIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={instance.topic || `New chat`}
                                      primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: selectedInstanceId === instance.id ? 600 : 400,
                                        noWrap: true,
                                        sx: { overflow: 'hidden', textOverflow: 'ellipsis' }
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                              {hasMore && (
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => {
                                      setVisibleInstancesCount(prev => ({
                                        ...prev,
                                        [expansionKey]: visibleCount + 5
                                      }));
                                    }}
                                    sx={{
                                      borderRadius: '6px',
                                      py: 0.5,
                                      justifyContent: 'center',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      }
                                    }}
                                  >
                                    <ListItemText
                                      primary={`Show more`}
                                      primaryTypographyProps={{
                                        fontSize: '0.8rem',
                                        color: 'text.secondary',
                                        textAlign: 'center'
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )}
                            </>
                          );
                        })()}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    ));
  };

  const renderSkeletonItems = () => {
    return [0, 1, 2].map((sectionIdx) => (
      <React.Fragment key={`skeleton-section-${sectionIdx}`}>
        <ListItem disablePadding>
          <ListItemButton>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
            />
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemButton>
        </ListItem>
        <Collapse in timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {[0, 1, 2].map((itemIdx) => (
              <ListItem key={`skeleton-item-${itemIdx}`} disablePadding>
                <ListItemButton sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <Skeleton variant="circular" width={35} height={35} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Skeleton variant="text" width="80%" />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    ));
  };

  return (
    <>
        <List disablePadding className="leftPanelLegacyList">
          {isLoading ? renderSkeletonItems() : getListItems()}
        </List>


      {dialogOpen && (
        <InputDialog
          open
          title={
            currentSection === "Projects"
              ? "Create Project"
              : `Create ${currentSection}`
          }
          label="Name"
          onSubmit={handleDialogSubmit}
          onClose={() => setDialogOpen(false)}
          hideNameInput={currentSection === "Direct Messages"}
        />
      )}

      {/* Rename Instance Dialog */}
      <EditDialog
        open={renameDialogOpen}
        title="Rename Chat"
        name={instanceToRename?.instance.topic || ''}
        onConfirm={handleRenameInstance}
        onClose={() => {
          setRenameDialogOpen(false);
          setInstanceToRename(null);
        }}
      />

      {/* Delete Instance Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Chat"
        message={`Are you sure you want to delete "${instanceToDelete?.instance.topic || 'this chat'}"? This action cannot be undone.`}
        color="error"
        onConfirm={handleDeleteInstance}
        onClose={() => {
          setDeleteDialogOpen(false);
          setInstanceToDelete(null);
        }}
      />

      <Notification
        {...notif}
        onClose={() => setNotif({ ...notif, open: false })}
      />
    </>
  );
};

export default LeftPanelLegacy;
